import { useState } from "react";
import { api } from "@/lib/kfmApi";
import { Textarea } from "@/components/ui/textarea";
import CopyButton from "@/components/CopyButton";
import HighlightableText from "@/components/HighlightableText";
import { toast } from "sonner";
import { Check, Pencil } from "lucide-react";

export default function ReflectField({ sessionId, sectionIndex, questionIndex, question, initial, highlights, onHighlightsChange, sharePath }) {
  const [value, setValue] = useState(initial || "");
  const [saved, setSaved] = useState(false);
  const [editingAnswer, setEditingAnswer] = useState(!(initial || "").trim());
  const qBlock = `reflect-${sectionIndex}-${questionIndex}-q`;
  const aBlock = `reflect-${sectionIndex}-${questionIndex}-a`;

  const save = async () => {
    try {
      await api.post(`/study/sessions/${sessionId}/answer`, {
        section_index: sectionIndex,
        question_index: questionIndex,
        answer: value,
      });
      if (value.trim()) {
        setSaved(true);
        setEditingAnswer(false);
        setTimeout(() => setSaved(false), 2500);
      }
    } catch {
      toast.error("Couldn't save your answer — try again");
    }
  };

  return (
    <div
      data-testid={`reflect-${sectionIndex}-${questionIndex}`}
      className="bg-[#141B29]/60 rounded-2xl p-5 border border-[#1E273D] space-y-3"
    >
      <div className="flex items-start justify-between gap-3">
        <p className="text-sm font-medium text-slate-300 leading-relaxed flex-1">
          <span className="text-[#00E5FF]">Reflect · </span>
          <HighlightableText
            sessionId={sessionId}
            block={qBlock}
            text={question}
            highlights={highlights}
            onChange={onHighlightsChange}
            sharePath={sharePath}
            cardKind="companion"
          />
        </p>
        <CopyButton text={question} label="Question copied" testid={`copy-question-${sectionIndex}-${questionIndex}`} />
      </div>

      {editingAnswer ? (
        <Textarea
          data-testid={`answer-${sectionIndex}-${questionIndex}`}
          autoFocus={!!(initial || "").trim()}
          value={value}
          onChange={(e) => setValue(e.target.value)}
          onBlur={save}
          placeholder="Type your reflection here…"
          rows={3}
          className="bg-[#0E131F] border-0 rounded-xl resize-none text-slate-200 focus-visible:ring-2 focus-visible:ring-[#00E5FF]/25"
        />
      ) : (
        <div
          data-testid={`answer-view-${sectionIndex}-${questionIndex}`}
          className="group relative bg-[#0E131F] rounded-xl px-3.5 py-3 text-sm text-slate-200 leading-relaxed whitespace-pre-wrap"
        >
          <HighlightableText
            sessionId={sessionId}
            block={aBlock}
            text={value}
            highlights={highlights}
            onChange={onHighlightsChange}
            sharePath={sharePath}
            cardKind="reflection"
          />
          <button
            type="button"
            data-testid={`answer-edit-${sectionIndex}-${questionIndex}`}
            onClick={() => setEditingAnswer(true)}
            title="Edit your answer"
            className="absolute top-2 right-2 p-1.5 rounded-full text-slate-500 hover:text-[#00E5FF] hover:bg-[#10222A] opacity-0 group-hover:opacity-100 transition-opacity"
          >
            <Pencil className="w-3.5 h-3.5" strokeWidth={1.75} />
          </button>
        </div>
      )}

      <div className="flex items-center justify-between">
        <span className="text-xs text-slate-500 flex items-center gap-1">
          {saved && (
            <>
              <Check className="w-3 h-3 text-[#00E5FF]" strokeWidth={2} /> Saved
            </>
          )}
        </span>
        {value.trim() && (
          <CopyButton text={value} label="Answer copied" testid={`copy-answer-${sectionIndex}-${questionIndex}`} />
        )}
      </div>
    </div>
  );
}
